import React, { useCallback } from 'react';
import { useAsyncValue, useRevalidator } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Components
import { MaterialsManagementTable } from '../../../../components/display/tables/formTables/managementTables/MaterialsManagementTable';
import { AddMaterialManagementForm } from '../../../../components/forms/AddMaterialManagementForm';
import { formMaterialManagementNaming } from '../../../../components/forms/AddMaterialManagementForm/constants';

// Types
import { MaterialManagementLoader } from './types';

// Styles
import { Container, MaterialsManagementSection } from './styles';

// GQL
import { client } from '../../../../gql/client';
import { MaterialManagement } from '../../../../components/forms/AddMaterialManagementForm/types';
import {
  ACTIVATE_MATERIAL,
  CREATE_NEW_MATERIAL,
  DEACTIVATE_MATERIAL,
} from '../../../../gql/mutations/materials';
import { NEW_MATERIAL } from '../../../../gql/fragments/material';

// Redux Actions
import { openModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../../../components/modals/SuperModal/types';

// Types
import { dataTable } from '../../../../components/display/tables/formTables/managementTables/MaterialsManagementTable/types';

export default function MaterialsManagementPage(): JSX.Element {
  const loaderData = useAsyncValue() as MaterialManagementLoader;
  const revalidator = useRevalidator();
  const dispatch = useDispatch();
  const allMaterialsNames = loaderData.data.materialsData.map(
    (material: { name: string }) =>
      material.name
        .toLowerCase()
        .split('')
        .filter((letter) => letter !== ' ')
        .join('')
  );

  const onReFetchData = useCallback(() => {
    revalidator.revalidate();
  }, []);

  const onFormSubmit = async (data: MaterialManagement) => {
    const parsedNameToCompare = data.name
      .toLowerCase()
      .split('')
      .filter((letter) => letter !== ' ')
      .join('');

    if (
      allMaterialsNames.some((item: string) => item === parsedNameToCompare)
    ) {
      return 'L’element ja existeix.';
    }

    await client.mutate({
      mutation: CREATE_NEW_MATERIAL,
      variables: {
        input: {
          name: data.name,
          measureTypeIDs: data.typeOfMeasurement,
          codificationIDs: data.codification,
          subCodificationIDs: data.subCodification,
        },
      },

      update(cache, { data: { createMaterial } }) {
        cache.modify({
          fields: {
            materials(existingMaterials = []) {
              const createMaterialRef = client.writeFragment({
                fragment: NEW_MATERIAL,
                data: createMaterial,
              });

              return [...existingMaterials, createMaterialRef];
            },
          },
        });
      },
    });

    onReFetchData();
  };

  // Materiales por equipo (2026-07-31): abre el modal de asignación. Los
  // equipos vienen del loader (Query.teams ya está acotada por tenant).
  const onEditMaterialTeams = useCallback(
    (material: dataTable) => {
      dispatch(
        openModal(modalIds.EDIT_MATERIAL_TEAMS_MODAL, {
          material: {
            id: material.id,
            materialId: material.materialId,
            name: material.name,
            teamIDs: material.teamIDs ?? [],
          },
          teams: loaderData.data.teams ?? [],
          onSuccess: onReFetchData,
        })
      );
    },
    [dispatch, loaderData.data.teams, onReFetchData]
  );

  /**
   * Baja/alta lógica del catálogo.
   *
   * **Sin confirmación a propósito**: es reversible en un clic desde la lista
   * de al lado. (Empresas externas sí confirma, pero lo suyo es un borrado
   * DURO — aquí no existe.)
   *
   * Se revalida al terminar para que las dos listas se repartan con el dato del
   * servidor y no con una suposición del cliente.
   */
  const setMaterialActive = useCallback(
    async (id: string, isActive: boolean) => {
      try {
        await client.mutate({
          mutation: isActive ? ACTIVATE_MATERIAL : DEACTIVATE_MATERIAL,
          variables: { id },
        });
      } finally {
        onReFetchData();
      }
    },
    [onReFetchData]
  );

  return (
    <Container>
      <MaterialsManagementSection>
        <AddMaterialManagementForm
          initialData={loaderData.cacheData}
          onFormSubmit={onFormSubmit}
          namingPersistForm={
            formMaterialManagementNaming.NEW_MATERIAL_MANAGEMENT_FORM
          }
          data={loaderData.data}
        />
      </MaterialsManagementSection>
      <MaterialsManagementSection>
        <MaterialsManagementTable
          data={{
            materials: loaderData.data.materialsData,
            activeMaterials: loaderData.data.activeMaterials,
            inactiveMaterials: loaderData.data.inactiveMaterials,
          }}
          onEditMaterialTeams={onEditMaterialTeams}
          onDeactivate={(id) => void setMaterialActive(id, false)}
          onActivate={(id) => void setMaterialActive(id, true)}
        />
      </MaterialsManagementSection>
    </Container>
  );
}
